import { useCallback, useEffect, useMemo, useState } from 'react';
import { FiMail, FiRefreshCw, FiShield, FiUserMinus, FiUserPlus, FiX } from 'react-icons/fi';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { SearchableSelect } from '../../components/ui/SearchableSelect';
import { EmpresaPermissionsCard } from '../Admin/AdminUsers';
import { PerfisDeAcessoPainel, type PerfisApi } from '../Admin/AdminAccessProfiles';
import adminStyles from '../Admin/Admin.module.css';
import { api } from '../../services/api';
import type { AccessProfile, ConviteDaEmpresa, EmpresaDoUsuario, MembroDaEmpresa, PermissionItem } from '../../types';

/**
 * Telas do DONO da empresa em Configurações (plano de convites, Fases 2 e 3):
 * quem participa da empresa, convites, e os perfis de acesso da empresa. Só
 * aparecem para o dono da empresa ativa; o backend recusa as rotas para
 * qualquer outro.
 */

export function PerfisDaEmpresa({ empresa }: { empresa: EmpresaDoUsuario }) {
    const perfisApi = useMemo<PerfisApi>(
        () => ({
            list: () => api.getPerfisDaEmpresa(empresa.id),
            create: (data) => api.criarPerfilDaEmpresa(empresa.id, data),
            update: (id, data) => api.atualizarPerfilDaEmpresa(empresa.id, id, data),
            duplicate: (id) => api.duplicarPerfilDaEmpresa(empresa.id, id),
            remove: (id) => api.excluirPerfilDaEmpresa(empresa.id, id),
        }),
        [empresa.id],
    );

    return (
        <>
            <p style={{ marginTop: 0, fontSize: 14, color: 'var(--color-gray-500)' }}>
                Defina o que cada perfil pode ver e fazer. Os perfis padrão servem para todas as empresas e não podem ser
                alterados: duplique um deles para criar uma versão da sua empresa.
            </p>
            <PerfisDeAcessoPainel api={perfisApi} />
        </>
    );
}

function formatarValidade(iso: string): string {
    const d = new Date(iso);
    return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

/** Modal de convite: e-mail, perfil e negócios que a pessoa vai ver. */
function ConvidarModal({
    empresa,
    perfis,
    aberto,
    onFechar,
    onConvidado,
}: {
    empresa: EmpresaDoUsuario;
    perfis: AccessProfile[];
    aberto: boolean;
    onFechar: () => void;
    onConvidado: (convite: ConviteDaEmpresa) => void;
}) {
    const [email, setEmail] = useState('');
    const [perfilId, setPerfilId] = useState('');
    const [filialIds, setFilialIds] = useState<string[]>([]);
    const [enviando, setEnviando] = useState(false);
    const [erro, setErro] = useState<string | null>(null);

    useEffect(() => {
        if (!aberto) return;
        setEmail('');
        setErro(null);
        // Padrões: Visualizador (o mais restrito) e todos os negócios.
        setPerfilId(perfis.find((p) => p.name === 'Visualizador' && p.isSystem)?.id ?? '');
        setFilialIds(empresa.filiais.map((f) => f.id));
    }, [aberto, perfis, empresa.filiais]);

    const alternarFilial = (id: string) =>
        setFilialIds((atuais) => (atuais.includes(id) ? atuais.filter((f) => f !== id) : [...atuais, id]));

    const enviar = async (e: React.FormEvent) => {
        e.preventDefault();
        if (filialIds.length === 0) {
            setErro('Escolha ao menos um negócio.');
            return;
        }
        setEnviando(true);
        setErro(null);
        try {
            const convite = await api.convidarParaEmpresa(empresa.id, {
                email: email.trim(),
                accessProfileId: perfilId || undefined,
                filialIds,
            });
            onConvidado(convite);
        } catch (err) {
            setErro(err instanceof Error ? err.message : 'Não foi possível enviar o convite');
        } finally {
            setEnviando(false);
        }
    };

    return (
        <Modal isOpen={aberto} onClose={onFechar} title="Convidar usuário" size="large">
            <form onSubmit={enviar}>
                <p style={{ marginTop: 0, fontSize: 13, color: 'var(--color-gray-500)' }}>
                    A pessoa recebe um link por e-mail, válido por 1 dia. Ela só passa a acessar depois de aceitar.
                </p>
                <div className={adminStyles.formGrid}>
                    <div className={`${adminStyles.formGroup} ${adminStyles.fullWidth}`}>
                        <Input
                            label="E-mail"
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="pessoa@exemplo.com"
                        />
                    </div>
                    <div className={`${adminStyles.formGroup} ${adminStyles.fullWidth}`}>
                        <SearchableSelect
                            label="Perfil de acesso"
                            value={perfilId}
                            onChange={setPerfilId}
                            options={perfis.map((p) => ({ value: p.id, label: p.name }))}
                            placeholder="Selecione um perfil..."
                            dropdownVariant="inline"
                        />
                    </div>
                    <div className={`${adminStyles.formGroup} ${adminStyles.fullWidth}`}>
                        <label className={adminStyles.formLabel}>Negócios que a pessoa vai ver</label>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 16px' }}>
                            {empresa.filiais.map((f) => (
                                <label key={f.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 14 }}>
                                    <input
                                        type="checkbox"
                                        checked={filialIds.includes(f.id)}
                                        onChange={() => alternarFilial(f.id)}
                                        aria-label={f.name}
                                    />
                                    {f.name}
                                </label>
                            ))}
                        </div>
                    </div>
                </div>
                {erro && <p className={adminStyles.formError}>{erro}</p>}
                <div className={adminStyles.modalFooter}>
                    <Button type="button" onClick={onFechar}>Cancelar</Button>
                    <Button type="submit" disabled={enviando}>{enviando ? 'Enviando...' : 'Enviar convite'}</Button>
                </div>
            </form>
        </Modal>
    );
}

export function UsuariosDaEmpresa({ empresa }: { empresa: EmpresaDoUsuario }) {
    const [membros, setMembros] = useState<MembroDaEmpresa[]>([]);
    const [convites, setConvites] = useState<ConviteDaEmpresa[]>([]);
    const [perfis, setPerfis] = useState<AccessProfile[]>([]);
    const [loading, setLoading] = useState(true);
    const [erro, setErro] = useState<string | null>(null);
    const [aviso, setAviso] = useState<string | null>(null);
    const [selecionado, setSelecionado] = useState<MembroDaEmpresa | null>(null);
    const [removendo, setRemovendo] = useState<MembroDaEmpresa | null>(null);
    const [convidando, setConvidando] = useState(false);
    const [ocupado, setOcupado] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setErro(null);
        try {
            const [m, c, p] = await Promise.all([
                api.getMembrosDaEmpresa(empresa.id),
                api.getConvitesDaEmpresa(empresa.id),
                api.getPerfisDaEmpresa(empresa.id),
            ]);
            setMembros(m);
            setConvites(c);
            setPerfis(p);
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível carregar os usuários');
        } finally {
            setLoading(false);
        }
    }, [empresa.id]);

    useEffect(() => { load(); }, [load]);

    const avisarEnvio = (convite: ConviteDaEmpresa) =>
        setAviso(
            convite.emailEnviado === false
                ? `Convite criado para ${convite.email}, mas o e-mail não pôde ser enviado agora. Use "Reenviar" daqui a pouco.`
                : `Convite enviado para ${convite.email}.`,
        );

    const salvar = async (
        _empresaId: string,
        data: { accessProfileId?: string; overrides: PermissionItem[]; filialIds?: string[] },
    ) => {
        if (!selecionado) return;
        await api.atualizarMembroDaEmpresa(empresa.id, selecionado.userId, data);
        await load();
        setSelecionado(null);
    };

    const reenviar = async (c: ConviteDaEmpresa) => {
        setOcupado(c.id);
        try {
            avisarEnvio(await api.reenviarConvite(empresa.id, c.id));
            await load();
        } catch (e) {
            setAviso(e instanceof Error ? e.message : 'Não foi possível reenviar');
        } finally {
            setOcupado(null);
        }
    };

    const cancelar = async (c: ConviteDaEmpresa) => {
        setOcupado(c.id);
        try {
            await api.cancelarConvite(empresa.id, c.id);
            setAviso(`Convite para ${c.email} cancelado.`);
            await load();
        } catch (e) {
            setAviso(e instanceof Error ? e.message : 'Não foi possível cancelar');
        } finally {
            setOcupado(null);
        }
    };

    const confirmarRemocao = async () => {
        if (!removendo) return;
        setOcupado(removendo.userId);
        try {
            await api.removerMembroDaEmpresa(empresa.id, removendo.userId);
            setAviso(`${removendo.name} não acessa mais a empresa.`);
            setRemovendo(null);
            await load();
        } catch (e) {
            setAviso(e instanceof Error ? e.message : 'Não foi possível remover');
        } finally {
            setOcupado(null);
        }
    };

    const outros = membros.filter((m) => !m.souDono);
    const limite = empresa.plano?.maxUsuarios ?? 0;
    const ocupadas = outros.length + convites.filter((c) => !c.expirado).length;

    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                <p style={{ margin: 0, fontSize: 14, color: 'var(--color-gray-500)', flex: '1 1 320px' }}>
                    Pessoas que acessam a sua empresa. Defina o perfil de cada uma e quais negócios ela vê. Você, como dono,
                    tem acesso total. {limite > 0 ? `Seu plano permite ${limite} pessoa(s) além de você (${ocupadas} em uso).` : ''}
                </p>
                <Button onClick={() => { setAviso(null); setConvidando(true); }} disabled={loading || ocupadas >= limite}>
                    <FiUserPlus size={16} style={{ marginRight: '0.5rem' }} />Convidar
                </Button>
            </div>
            {!loading && limite === 0 && (
                <p style={{ fontSize: 13, color: 'var(--color-gray-500)' }}>Seu plano não permite convidar usuários.</p>
            )}

            {aviso && (
                <p role="status" style={{ margin: '12px 0', padding: '8px 12px', borderRadius: 8, background: 'var(--color-gray-100, #f3f4f6)', fontSize: 13 }}>
                    {aviso}
                </p>
            )}
            {loading && <div className={adminStyles.loadingState}>Carregando...</div>}
            {erro && <p className={adminStyles.formError}>{erro}</p>}

            {!loading && !erro && (
                <>
                    <div className={adminStyles.usersSection} style={{ marginTop: 16 }}>
                        <table className={adminStyles.usersTable}>
                            <thead>
                                <tr>
                                    <th>Usuário</th>
                                    <th>Perfil</th>
                                    <th>Negócios</th>
                                    <th>Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {outros.map((m) => (
                                    <tr key={m.userId}>
                                        <td>
                                            <div className={adminStyles.userInfo}>
                                                <div className={adminStyles.userAvatar}>{m.name.slice(0, 2).toUpperCase()}</div>
                                                <div className={adminStyles.userDetails}>
                                                    <div className={adminStyles.name}>{m.name}</div>
                                                    <div className={adminStyles.email}>{m.email}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td>{m.accessProfile?.name ?? 'Padrão do papel'}</td>
                                        <td>{m.filiais.map((f) => f.name).join(', ') || '—'}</td>
                                        <td>
                                            <div className={adminStyles.actionButtons}>
                                                <button className={adminStyles.actionBtn} onClick={() => setSelecionado(m)} title="Gerenciar acessos">
                                                    <FiShield size={14} /> Acessos
                                                </button>
                                                <button
                                                    className={`${adminStyles.actionBtn} ${adminStyles.deleteBtn}`}
                                                    onClick={() => setRemovendo(m)}
                                                    disabled={ocupado === m.userId}
                                                    title="Tirar da empresa"
                                                >
                                                    <FiUserMinus size={14} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {outros.length === 0 && (
                                    <tr>
                                        <td colSpan={4} className={adminStyles.emptyRow}>
                                            Ninguém além de você acessa esta empresa ainda.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {convites.length > 0 && (
                        <>
                            <h4 style={{ margin: '24px 0 8px' }}>Convites pendentes</h4>
                            <div className={adminStyles.usersSection}>
                                <table className={adminStyles.usersTable}>
                                    <thead>
                                        <tr>
                                            <th>E-mail</th>
                                            <th>Perfil</th>
                                            <th>Validade</th>
                                            <th>Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {convites.map((c) => (
                                            <tr key={c.id}>
                                                <td><FiMail size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />{c.email}</td>
                                                <td>{c.accessProfile?.name ?? 'Padrão'}</td>
                                                <td>{c.expirado ? 'Expirou' : `Até ${formatarValidade(c.expiraEm)}`}</td>
                                                <td>
                                                    <div className={adminStyles.actionButtons}>
                                                        <button className={adminStyles.actionBtn} disabled={ocupado === c.id} onClick={() => reenviar(c)} title="Reenviar (link novo, mais 1 dia)">
                                                            <FiRefreshCw size={14} />
                                                        </button>
                                                        <button
                                                            className={`${adminStyles.actionBtn} ${adminStyles.deleteBtn}`}
                                                            disabled={ocupado === c.id}
                                                            onClick={() => cancelar(c)}
                                                            title="Cancelar convite"
                                                        >
                                                            <FiX size={14} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </>
            )}

            <ConvidarModal
                empresa={empresa}
                perfis={perfis}
                aberto={convidando}
                onFechar={() => setConvidando(false)}
                onConvidado={(convite) => {
                    setConvidando(false);
                    avisarEnvio(convite);
                    load();
                }}
            />

            <Modal
                isOpen={!!selecionado}
                onClose={() => setSelecionado(null)}
                title={selecionado ? `Acessos de ${selecionado.name}` : ''}
                size="xlarge"
                scrollBody
            >
                {selecionado && (
                    <EmpresaPermissionsCard
                        empresa={{
                            empresaId: empresa.id,
                            empresaName: empresa.name,
                            souDono: selecionado.souDono,
                            filiais: selecionado.filiais,
                            accessProfile: selecionado.accessProfile,
                            overrides: selecionado.overrides,
                            divergente: false,
                        }}
                        profiles={perfis}
                        onSave={salvar}
                        filiaisDisponiveis={empresa.filiais}
                    />
                )}
            </Modal>

            <Modal isOpen={!!removendo} onClose={() => setRemovendo(null)} title="Tirar da empresa">
                <div style={{ padding: '0.5rem 0 1rem' }}>
                    <p>
                        <strong>{removendo?.name}</strong> deixa de acessar esta empresa. Os lançamentos que fez continuam
                        aqui, e nada muda nas outras empresas dela. Para voltar, basta convidar de novo.
                    </p>
                </div>
                <div className={adminStyles.modalFooter}>
                    <Button type="button" onClick={() => setRemovendo(null)}>Cancelar</Button>
                    <Button type="button" disabled={!!ocupado} onClick={confirmarRemocao}>
                        {ocupado ? 'Removendo...' : 'Tirar da empresa'}
                    </Button>
                </div>
            </Modal>
        </>
    );
}
